import { useCallback, useMemo } from 'react';
import portfolioMap from '../work_list/portfolioMap.json';
import portfolioRoutes from '../work_list/portfolioRoutes.json';
import workDetails from '../work_list/allWorkData.json';
import {
  PortfolioCategory,
  PortfolioCategoryWithMatrix,
  PortfolioCode,
  PortfolioItem,
  PortfolioRouteConfig,
  PortfolioRouteEntry,
  CvRouteValue,
  CvSettings,
  ExperienceOverride,
  WorkDetail,
  PortfolioHomeContent,
  PortfolioHomeConfig,
  PortfolioFooterConfig,
  PortfolioFooterContent,
  SkillGroupData,
  BlobConfig,
} from '../types/portfolio';
import {
  WORK_IMAGE_MAP,
  createFallbackDetail,
} from '../utils/portfolioAssets';
import { DEFAULT_BLOBS } from '../constants/portfolioDefaults';

const ROUTE_CONFIG = portfolioRoutes as PortfolioRouteConfig;
const DEFAULT_ROUTE_ENTRY: PortfolioRouteEntry = ROUTE_CONFIG.default ?? {};
const loadCvAssets = (): Record<string, string> => {
  try {
    const context = require.context('../asset/cv', false, /\.pdf$/);
    return context.keys().reduce<Record<string, string>>((acc, key) => {
      const assetKey = key.replace('./', '');
      try {
        acc[assetKey] = context(key) as string;
      } catch {
        // Skip missing assets so build continues even if files were removed.
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const CV_ASSETS = loadCvAssets();

const workDetailMap = workDetails as Record<string, WorkDetail>;

const getPortfolioName = (code: string): string | undefined =>
  portfolioMap[code as keyof typeof portfolioMap];

const defaultCodes = Object.keys(portfolioMap).sort((a, b) =>
  a.localeCompare(b),
);


const DEFAULT_HOME_CONTENT: PortfolioHomeContent = {
  badge: '李佩恩作品集',
  title: '設計研究X數據分析',
  intro: [
    '我是佩恩，擁有 3 年半的工業設計實務經驗，參與從設計研發到量產的完整流程。在這段歷程中，我從競品分析出發，逐步開啟了數據分析的路線。',
    '研究所期間專注於使用者研究與數據 / AI 應用，結合設計研究、資料洞察與技術評估，嘗試以跨領域視角發掘問題、提出解方。',
    '期待與你分享這些專案與思考，並探索更多合作的可能。',
  ],
  blobs: DEFAULT_BLOBS,
};

const DEFAULT_FOOTER_CONTENT: PortfolioFooterContent = {
  title: '我是佩恩',
  message: '感謝您的閱讀，歡迎與我交流',
  email: 'peien.li0114@gmail.com',
};

const normalizeHomeIntro = (value?: string | string[]): string[] => {
  if (!value) {
    return [];
  }
  const source = Array.isArray(value) ? value : value.split(/\n+/);
  return source
    .map((item) => item.replace(/\r/g, '').trim())
    .filter(Boolean);
};

const normaliseHomeContent = (
  config: PortfolioHomeConfig | undefined,
  fallback: PortfolioHomeContent,
  routeBlobs?: BlobConfig[],
): PortfolioHomeContent => {
  const badge = config?.badge?.trim() || fallback.badge;
  const title = config?.title?.trim() || fallback.title;
  const intro = normalizeHomeIntro(config?.intro);
  // Priority: home.blobs > route.blobs > fallback.blobs
  const blobs = (config?.blobs && config.blobs.length > 0)
    ? config.blobs
    : (routeBlobs && routeBlobs.length > 0)
      ? routeBlobs
      : fallback.blobs;
  return {
    badge,
    title,
    intro: intro.length ? intro : fallback.intro,
    blobs,
  };
};

const normaliseFooterContent = (
  config: PortfolioFooterConfig | undefined,
  fallback: PortfolioFooterContent,
): PortfolioFooterContent => {
  const title = config?.title?.trim() || fallback.title;
  const message = config?.message?.trim() || fallback.message;
  const email = config?.email?.trim() || fallback.email;
  return {
    title,
    message,
    email,
  };
};

const buildCategories = (
  source: Record<string, PortfolioCode[]> | undefined,
): PortfolioCategory[] => {
  if (!source) {
    return [];
  }

  const normalized = new Map(
    defaultCodes.map((code) => [code.toLowerCase(), code]),
  );

  const globalSeen = new Set<string>();
  const result: PortfolioCategory[] = [];

  Object.entries(source).forEach(([categoryName, codes = []]) => {
    if (!Array.isArray(codes)) {
      return;
    }

    const items: PortfolioItem[] = [];
    codes.forEach((rawCode) => {
      const lookupKey = rawCode.trim().toLowerCase();
      const resolved = normalized.get(lookupKey);
      if (!resolved || globalSeen.has(resolved)) {
        return;
      }
      const name = getPortfolioName(resolved);
      if (!name) {
        return;
      }
      const detail = workDetailMap[resolved] ?? createFallbackDetail(name);
      globalSeen.add(resolved);
      items.push({
        code: resolved,
        name,
        category: categoryName,
        detail,
      });
    });

    if (items.length) {
      result.push({ name: categoryName, items });
    }
  });

  return result;
};

const normaliseCvRoute = (
  config: CvRouteValue | undefined,
): {
  asset?: string;
  link?: string;
  experienceOverrides?: Record<string, ExperienceOverride>;
  showLink?: boolean;
} => {
  if (!config) {
    return {};
  }
  if (typeof config === 'string') {
    return { asset: config.trim() || undefined };
  }
  const asset = config.asset?.trim() || undefined;
  const link = config.link?.trim() || undefined;
  const experienceOverrides =
    config.experienceOverrides && Object.keys(config.experienceOverrides).length > 0
      ? config.experienceOverrides
      : undefined;
  const showLink = typeof config === 'object' ? config.showLink : undefined;
  return { asset, link, experienceOverrides, showLink };
};

export const usePortfolioData = (routeKey: string) => {
  const currentEntry = useMemo<PortfolioRouteEntry>(
    () => ROUTE_CONFIG[routeKey] ?? {},
    [routeKey],
  );
  const defaultCvConfig = useMemo(
    () => normaliseCvRoute(DEFAULT_ROUTE_ENTRY.cv),
    [],
  );

  const categories = useMemo<PortfolioCategory[]>(() => {
    let resolvedCategories = buildCategories(currentEntry.categories);

    if (!resolvedCategories.length && currentEntry !== DEFAULT_ROUTE_ENTRY) {
      resolvedCategories = buildCategories(DEFAULT_ROUTE_ENTRY.categories);
    }

    if (!resolvedCategories.length) {
      const fallbackItems = defaultCodes
        .map((code) => {
          const name = getPortfolioName(code);
          if (!name) {
            return null;
          }
          return {
            code,
            name,
            category: '作品集',
          };
        })
        .filter((item): item is PortfolioItem => item !== null);

      return fallbackItems.length
        ? [
          {
            name: '作品集',
            items: fallbackItems.map((item) => ({
              ...item,
              detail: workDetailMap[item.code] ?? createFallbackDetail(item.name),
            })),
          },
        ]
        : [];
    }

    // Force "其他作品專案" to be the last category if it exists
    const SPECIAL_CATEGORY_NAME = '其他作品專案';
    const specialIndex = resolvedCategories.findIndex(c => c.name === SPECIAL_CATEGORY_NAME);
    if (specialIndex > -1 && specialIndex !== resolvedCategories.length - 1) {
      const [special] = resolvedCategories.splice(specialIndex, 1);
      resolvedCategories.push(special);
    }

    return resolvedCategories;
  }, [currentEntry]);

  const categoriesWithMatrix = useMemo<PortfolioCategoryWithMatrix[]>(
    () =>
      categories.map((category) => ({
        ...category,
        itemsMap: category.items.reduce<Record<string, PortfolioItem>>(
          (acc, item) => {
            acc[item.code] = item;
            return acc;
          },
          {},
        ),
      })),
    [categories],
  );

  const portfolioItems = useMemo(() => {
    const seen = new Set<string>();
    return categoriesWithMatrix
      .flatMap((category) => category.items)
      .filter((item) => {
        if (seen.has(item.code)) {
          return false;
        }
        seen.add(item.code);
        return true;
      });
  }, [categoriesWithMatrix]);

  const getYearRangeText = useCallback((detail: WorkDetail): string => {
    const start = (detail.yearBegin || '').trim();
    const end = (detail.yearEnd || '').trim();
    if (start && end && start !== end) {
      return `${start} – ${end}`;
    }
    return start || end || '';
  }, []);

  const cvSettings = useMemo<CvSettings>(() => {
    const currentConfig = normaliseCvRoute(currentEntry.cv);

    const resolvedAssetKey =
      currentConfig.asset ??
      defaultCvConfig.asset ??
      (typeof DEFAULT_ROUTE_ENTRY.cv === 'string'
        ? DEFAULT_ROUTE_ENTRY.cv
        : undefined);

    const downloadUrl =
      resolvedAssetKey && CV_ASSETS[resolvedAssetKey]
        ? CV_ASSETS[resolvedAssetKey]
        : null;

    const resolvedLink =
      currentConfig.link ?? defaultCvConfig.link ?? null;

    const resolvedExperienceOverrides =
      currentConfig.experienceOverrides ??
      defaultCvConfig.experienceOverrides ??
      null;

    const showLink = currentConfig.showLink ?? defaultCvConfig.showLink ?? true;

    return {
      downloadUrl,
      link: resolvedLink,
      experienceOverrides: resolvedExperienceOverrides,
      showLink,
    };
  }, [currentEntry, defaultCvConfig]);

  const defaultHomeContent = useMemo(
    () => normaliseHomeContent(DEFAULT_ROUTE_ENTRY.home, DEFAULT_HOME_CONTENT, DEFAULT_ROUTE_ENTRY.blobs),
    [],
  );

  const homeContent = useMemo(
    () => normaliseHomeContent(currentEntry.home, defaultHomeContent, currentEntry.blobs),
    [currentEntry.home, currentEntry.blobs, defaultHomeContent],
  );

  const defaultFooterContent = useMemo(
    () => normaliseFooterContent(DEFAULT_ROUTE_ENTRY.footer, DEFAULT_FOOTER_CONTENT),
    [],
  );

  const footerContent = useMemo(
    () => normaliseFooterContent(currentEntry.footer, defaultFooterContent),
    [currentEntry.footer, defaultFooterContent],
  );

  const lang = currentEntry.lang || 'zh';
  const siteTitle = lang === 'en'
    ? (currentEntry.siteTitleEn?.trim() || currentEntry.siteTitle?.trim() || DEFAULT_ROUTE_ENTRY.siteTitle?.trim() || 'PEI-EN')
    : (currentEntry.siteTitle?.trim() || DEFAULT_ROUTE_ENTRY.siteTitle?.trim() || 'PEI-EN');
  const sidebarTitle = lang === 'en'
    ? (currentEntry.sidebarTitleEn?.trim() || currentEntry.sidebarTitle?.trim() || DEFAULT_ROUTE_ENTRY.sidebarTitle?.trim() || 'PEI EN LI')
    : (currentEntry.sidebarTitle?.trim() || DEFAULT_ROUTE_ENTRY.sidebarTitle?.trim() || 'PEI EN LI');
  const cvSummary = Array.isArray(currentEntry.cvSummary)
    ? currentEntry.cvSummary
    : Array.isArray(DEFAULT_ROUTE_ENTRY.cvSummary)
      ? DEFAULT_ROUTE_ENTRY.cvSummary
      : null;

  const routeSkills: SkillGroupData[] | null = useMemo(() => {
    if (Array.isArray(currentEntry.skills) && currentEntry.skills.length > 0) {
      return currentEntry.skills;
    }
    return null;
  }, [currentEntry.skills]);



  return {
    categories,
    categoriesWithMatrix,
    portfolioItems,
    workDetailMap,
    workImageMap: WORK_IMAGE_MAP,
    getYearRangeText,
    cvSettings,
    homeContent,
    footerContent,
    siteTitle,
    sidebarTitle,
    cvSummary,
    routeSkills,
    lang,
  };
};
